import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { CompanyServiceReport } from "../../api/serviceReportsApi";

// ─── Types ──────────────────────────────────────────────────

interface NoteEntry {
  date: string;
  text: string;
}

interface InlineNoteSectionProps {
  sr: CompanyServiceReport;
  isPhone: boolean;
  translate: (key: string) => string;
  /** Whether the current tab allows adding notes (kept for API compat, unused in UI). */
  canAddNote: boolean;
  onAddNote: (noteText: string) => Promise<void>;
}

// ─── Helpers ────────────────────────────────────────────────

/** Parse notes into structured entries (without author — per Paper design).
 *  Splits by "Name — DD MMM YYYY, HH:mm" headers; text below becomes entry. */
function parseNotes(notes: string | undefined): NoteEntry[] {
  if (!notes || !notes.trim()) return [];

  const entryPattern = /^(.+?)\s*[—–-]\s*(\d{1,2}\s+\w{3}\s+\d{4},?\s*\d{2}:\d{2})\s*$/m;
  const lines = notes.split("\n");

  const entries: NoteEntry[] = [];
  let currentDate = "";
  let currentTextLines: string[] = [];

  for (const line of lines) {
    const match = line.match(entryPattern);
    if (match) {
      if (currentTextLines.length > 0 || currentDate) {
        entries.push({ date: currentDate, text: currentTextLines.join("\n").trim() });
      }
      currentDate = match[2].trim();
      currentTextLines = [];
    } else {
      if (line.trim() || currentTextLines.length > 0) {
        currentTextLines.push(line);
      }
    }
  }

  if (currentTextLines.length > 0 || currentDate) {
    entries.push({ date: currentDate, text: currentTextLines.join("\n").trim() });
  }

  if (entries.length === 0) {
    return [{ date: "", text: notes.trim() }];
  }

  return entries.filter((e) => e.text.trim());
}

// ─── Component ──────────────────────────────────────────────

const InlineNoteSection: React.FC<InlineNoteSectionProps> = ({ sr, isPhone, translate }) => {
  const entries = parseNotes(sr.notes);
  if (entries.length === 0) return null;

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      {entries.map((entry, idx) => (
        <Box key={idx} sx={{ marginBottom: "10px" }}>
          {/* TECHNICAL NOTES label + date row — ABOVE the accent block */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "8px",
              flexWrap: "wrap",
              gap: "6px",
            }}
          >
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "1.2px",
                lineHeight: "14px",
                color: "#3498DB",
              }}
            >
              {translate("serviceReports.expanded.technicalNotes")}
            </Box>
            {entry.date && (
              <Box
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "12px",
                  color: dt.textMuted,
                  lineHeight: "16px",
                }}
              >
                {entry.date}
              </Box>
            )}
          </Box>
          {/* Blue accent box with note text */}
          <Box
            sx={{
              borderLeft: "3px solid #3498DB",
              padding: isPhone ? "10px 12px" : "14px 16px",
              backgroundColor: dt.inputBg,
              borderRadius: dt.radiusSm,
            }}
          >
            {/* Note text — Sora 13px per Paper */}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                fontWeight: 400,
                color: "#4A4F57",
                lineHeight: "160%",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {entry.text}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default InlineNoteSection;
